import {
  Popover,
  PopoverContentProps,
  Portal,
  UseDisclosureReturn,
  usePrevious
} from "@chakra-ui/react"
import { forwardRef, useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"
import AugmentationPositionedToolbar from "./AugmentationPositionedToolbar"
import AugmentationToolbar, {
  AugmentationToolbarHookReturn
} from "./AugmentationToolbar"

// Could consider a theme variant for this?
export const AugmentationToolbarPopoverContentProps: PopoverContentProps = {
  width: "auto",
  bg: "transparent",
  boxShadow: "none",
  border: "none",
  p: 0
}

function AugmentationToolbarContainer({
  toolbar
}: {
  toolbar: AugmentationToolbarHookReturn
}) {
  // Track the toolbar selected key to avoid a flash of the detailed view.
  // Once a toolbar option is selected, disable detailed mode. The toolbar
  // will not return to detailed mode unless remounted.
  const enableDetailedModeRef = useRef<boolean>(true)
  if (toolbar.selectedKey) enableDetailedModeRef.current = false

  return (
    <AugmentationPositionedToolbar
      toolbar={toolbar}
      detailed={enableDetailedModeRef.current}
      alwaysVisible
      allowOverlay
      gutter={10}
      components={{
        // Using standard AugmentationToolbar for side position as we are positioning the AugmentationToolbar relative to the popover.
        side: AugmentationToolbar,
        // Using positioned AugmentationToolbar for bottom position which is not relative to the popover, but rather posited fixed to bottom of screen.
        // Avoiding positioning to bottom if in detailed mode so the toolbar is positioned relative to the popover trigger.
        bottom: enableDetailedModeRef.current ? AugmentationToolbar : undefined
      }}
    />
  )
}

type AugmentationToolbarPopoverProps = {
  toolbar: AugmentationToolbarHookReturn
  disclosure: UseDisclosureReturn
  children: React.ReactNode
}

const AugmentationToolbarPopover = forwardRef<
  HTMLDivElement,
  AugmentationToolbarPopoverProps
>(({ toolbar, disclosure, children }, ref) => {
  const location = useLocation()
  const prevLocation = usePrevious(location)

  // Infer the popover should be closed when the location changes.
  // This ensures, for example, that navigating to /auth/sign-out/ will close the popover.
  useEffect(() => {
    if (prevLocation?.pathname !== location.pathname) {
      disclosure.onClose()
    }
  }, [prevLocation, location])

  // When you close the augmentaiton, the toolbar is no longer selected,
  // but the popover is still open.
  useEffect(() => {
    if (
      !toolbar.selectedKey ||
      !toolbar.selectedOption?.AugmentationComponent
    ) {
      disclosure.onClose()
    } else if (toolbar.selectedKey) {
      disclosure.onOpen()
    }
  }, [toolbar.selectedKey])

  // Prevent closing the popover when a toolbar option is selected, which would leave the toolbar in a selected state,
  // (with augmentation panel open) but no toolbar option selected (in a collapsed state).
  // Possibly, a better UX would be to also ensure we close the panel when closing the popover, but noting that simply
  // unselecting the toolbar option does not accomplish this (the panel would remain open, at given url).
  function handleToggle() {
    if (!toolbar.selectedKey) {
      disclosure.onToggle()
    } else {
      disclosure.onOpen()
    }
  }

  return (
    <Popover.Root
      {...disclosure}
      positioning={{ placement: toolbar.selectedKey ? "bottom" : "bottom-end" }}
      closeOnEscape={!toolbar.selectedKey}
      closeOnInteractOutside={!toolbar.selectedKey}
      // Lazy props for ensuring toolbar animations execute properly.
      lazyMount
      unmountOnExit
    >
      <Popover.Trigger
        asChild
        onClick={handleToggle}
        className="AugmentationToolbarPopoverTrigger"
      >
        {children}
      </Popover.Trigger>
      {/* Portaling to avoid a scale transform, so we could remove the Portal if there's another workaround. */}
      <Portal>
        <Popover.Positioner className="AugmentationToolbarPopover">
          <Popover.Content
            ref={ref}
            {...AugmentationToolbarPopoverContentProps}
          >
            <Popover.Body p={0}>
              <AugmentationToolbarContainer toolbar={toolbar} />
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
})

export default AugmentationToolbarPopover
